import { BellOutlined } from '@ant-design/icons'
import { notification } from 'antd'
import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from './notifications/context'
import {
  NotificationSoundPlayback,
  initNotificationAudioContext,
  playNotificationSound,
  preloadNotificationSound,
} from './notifications/sound'
import { NotificationItem } from './notifications/types'
import { isViewingNotificationTarget } from './notifications/notification-target'
import {
  isRealtimeSuspended,
  onRealtimeVisibilityChange,
} from '../../../lib/utils/realtime-visibility'

export const RoleNotificationsListener = () => {
  const navigate = useNavigate()
  const {
    addRealtimeNotification,
    hasRoleNotificationAccess,
    markAsRead,
    settingsLoaded,
    soundKey,
  } = useNotifications()
  const audioContextRef = useRef<AudioContext | null>(null)
  const playbackRef = useRef<NotificationSoundPlayback | null>(null)
  const soundRequestRef = useRef(0)

  const initAudioContext = useCallback(() => {
    initNotificationAudioContext(audioContextRef)
    preloadNotificationSound(audioContextRef, soundKey).catch(err => {
      console.error('[Notifications] Sound preload error:', err)
    })
  }, [soundKey])

  const playSound = useCallback(async () => {
    const requestId = ++soundRequestRef.current
    playbackRef.current?.stop()
    playbackRef.current = null

    const playback = await playNotificationSound(audioContextRef, soundKey)
    if (!playback || requestId !== soundRequestRef.current) {
      playback?.stop()
      return
    }
    playbackRef.current = playback
  }, [soundKey])

  useEffect(() => {
    window.addEventListener('pointerdown', initAudioContext, { once: true })
    window.addEventListener('keydown', initAudioContext, { once: true })

    return () => {
      window.removeEventListener('pointerdown', initAudioContext)
      window.removeEventListener('keydown', initAudioContext)
    }
  }, [initAudioContext])

  useEffect(() => {
    soundRequestRef.current += 1
    playbackRef.current?.stop()
    playbackRef.current = null
    preloadNotificationSound(audioContextRef, soundKey).catch(err => {
      console.error('[Notifications] Sound preload error:', err)
    })
  }, [soundKey])

  useEffect(
    () => () => {
      soundRequestRef.current += 1
      playbackRef.current?.stop()
      audioContextRef.current?.close().catch(() => undefined)
    },
    [],
  )

  useEffect(() => {
    const url = '/api/notifications/live'
    let eventSource: EventSource | null = null
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
    let reconnectDelay = 1000
    const maxReconnectDelay = 30000
    let isMounted = true

    if (!settingsLoaded || !hasRoleNotificationAccess) {
      return () => {
        isMounted = false
      }
    }

    const connect = () => {
      if (!isMounted || isRealtimeSuspended()) {
        return
      }

      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId)
        reconnectTimeoutId = null
      }

      eventSource?.close()
      eventSource = new EventSource(url, { withCredentials: true })

      eventSource.addEventListener('roleNotification', event => {
        let data: NotificationItem
        try {
          data = JSON.parse(event.data) as NotificationItem
        } catch (err) {
          console.error('[Notifications] SSE parse error:', err)
          return
        }

        if (isViewingNotificationTarget(data, window.location.pathname, window.location.search)) {
          markAsRead(data.id)
            .then(item => {
              if (item) {
                addRealtimeNotification(item)
              }
            })
            .catch(err => {
              addRealtimeNotification(data)
              console.error('[Notifications] Current chat read error:', err)
            })
          return
        }

        addRealtimeNotification(data)

        const onClick = async () => {
          await markAsRead(data.id).catch(err => {
            console.error('[Notifications] Toast read error:', err)
          })
          if (data.url) {
            navigate(data.url)
          }
        }

        notification.open({
          key: data.id,
          message: data.title,
          description: data.message,
          duration: 8,
          icon: <BellOutlined style={{ color: '#1677ff' }} />,
          onClick,
          style: { cursor: data.url ? 'pointer' : 'default' },
        })

        if (data.sound) {
          playSound().catch(err => {
            console.error('[Notifications] Sound playback error:', err)
          })
        }
      })

      eventSource.onopen = () => {
        reconnectDelay = 1000
      }

      eventSource.onerror = () => {
        eventSource?.close()
        eventSource = null
        if (!isMounted) {
          return
        }
        if (reconnectTimeoutId) {
          clearTimeout(reconnectTimeoutId)
        }
        reconnectTimeoutId = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 1.5, maxReconnectDelay)
          connect()
        }, reconnectDelay)
      }
    }

    const suspend = () => {
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId)
        reconnectTimeoutId = null
      }
      eventSource?.close()
      eventSource = null
    }

    const removeVisibilityListener = onRealtimeVisibilityChange(() => {
      if (isRealtimeSuspended()) {
        suspend()
      } else {
        connect()
      }
    })

    connect()

    return () => {
      isMounted = false
      removeVisibilityListener()
      suspend()
    }
  }, [
    addRealtimeNotification,
    hasRoleNotificationAccess,
    markAsRead,
    navigate,
    playSound,
    settingsLoaded,
  ])

  return null
}
